import { useEffect, useRef, useState } from 'react';
import { HOME_ROOM, type HomeDto, type InventoryItemDto } from '@bb/shared';
import { api, ApiError } from '../net/api';
import { drawFurniture, furnitureSize } from '../art/items';
import { Thumb } from './Thumb';

type Rotation = 0 | 90 | 180 | 270;
interface Piece {
  id: number;
  item: string;
  x: number;
  y: number;
  rotation: Rotation;
}

const WALL_H = 150;
let nextId = 1;

function size(item: InventoryItemDto | undefined, rotation: Rotation) {
  const s = furnitureSize(item?.render ?? {});
  return rotation % 180 === 0 ? s : { w: s.h, h: s.w };
}

/** The player's Cove Cottage. Changes are local until Save; the server checks ownership. */
export function HomeEditor({ ownerName, onLeave, onShop, onFarm }: { ownerName: string; onLeave: () => void; onShop: () => void; onFarm: () => void }) {
  const [home, setHome] = useState<HomeDto>();
  const [pieces, setPieces] = useState<Piece[]>([]);
  const [selected, setSelected] = useState<number>();
  const [dirty, setDirty] = useState(false);
  const [message, setMessage] = useState<{ kind: 'ok' | 'err'; text: string }>();
  const canvas = useRef<HTMLCanvasElement>(null);
  const drag = useRef<{ id: number; dx: number; dy: number } | null>(null);

  async function load() {
    try {
      const h = await api.home();
      setHome(h);
      setPieces(h.placements.map((p) => ({ ...p, id: nextId++ })));
      setDirty(false);
    } catch (e) {
      setMessage({ kind: 'err', text: (e as Error).message });
    }
  }
  useEffect(() => {
    void load();
  }, []);

  const byKey = new Map(home?.furniture.map((f) => [f.key, f]));
  const placedOf = (key: string) => pieces.filter((p) => p.item === key).length;

  // Draw.
  useEffect(() => {
    const el = canvas.current;
    if (!el) return;
    const dpr = window.devicePixelRatio || 1;
    el.width = HOME_ROOM.width * dpr;
    el.height = HOME_ROOM.height * dpr;
    const c = el.getContext('2d')!;
    c.setTransform(dpr, 0, 0, dpr, 0, 0);
    // Wall and window.
    c.fillStyle = '#f6e7cf';
    c.fillRect(0, 0, HOME_ROOM.width, WALL_H);
    c.fillStyle = '#e9d5b3';
    for (let x = 0; x < HOME_ROOM.width; x += 40) c.fillRect(x, 0, 2, WALL_H);
    c.fillStyle = '#9fdde6';
    c.fillRect(HOME_ROOM.width / 2 - 90, 24, 180, 96);
    c.fillStyle = '#ffffff';
    c.fillRect(HOME_ROOM.width / 2 - 2, 24, 4, 96);
    c.fillRect(HOME_ROOM.width / 2 - 90, 70, 180, 4);
    // Floor planks.
    c.fillStyle = '#d9b48a';
    c.fillRect(0, WALL_H, HOME_ROOM.width, HOME_ROOM.height - WALL_H);
    c.fillStyle = '#c99f72';
    for (let y = WALL_H + 30; y < HOME_ROOM.height; y += 30) c.fillRect(0, y, HOME_ROOM.width, 2);
    // Furniture, back to front.
    const sorted = [...pieces].sort((a, b) => a.y - b.y);
    for (const p of sorted) {
      const item = byKey.get(p.item);
      if (!item) continue;
      c.save();
      c.translate(p.x, p.y);
      c.rotate((p.rotation * Math.PI) / 180);
      drawFurniture(c, item.render);
      c.restore();
      if (p.id === selected) {
        const s = size(item, p.rotation);
        c.strokeStyle = '#e9a915';
        c.lineWidth = 3;
        c.setLineDash([6, 4]);
        c.strokeRect(p.x - s.w / 2 - 6, p.y - s.h / 2 - 6, s.w + 12, s.h + 12);
        c.setLineDash([]);
      }
    }
  }, [pieces, selected, home]);

  function toRoom(e: React.PointerEvent<HTMLCanvasElement>) {
    const r = e.currentTarget.getBoundingClientRect();
    return { x: ((e.clientX - r.left) / r.width) * HOME_ROOM.width, y: ((e.clientY - r.top) / r.height) * HOME_ROOM.height };
  }

  function hit(x: number, y: number) {
    const sorted = [...pieces].sort((a, b) => b.y - a.y);
    return sorted.find((p) => {
      const s = size(byKey.get(p.item), p.rotation);
      return Math.abs(x - p.x) <= s.w / 2 && Math.abs(y - p.y) <= s.h / 2;
    });
  }

  const clamp = (v: number, max: number) => Math.round(Math.min(max, Math.max(0, v)));
  const update = (id: number, change: Partial<Piece>) => {
    setPieces((ps) => ps.map((p) => (p.id === id ? { ...p, ...change } : p)));
    setDirty(true);
  };

  function add(f: InventoryItemDto) {
    if (placedOf(f.key) >= f.quantity) return;
    const id = nextId++;
    setPieces((ps) => [...ps, { id, item: f.key, x: HOME_ROOM.width / 2, y: (HOME_ROOM.height + WALL_H) / 2, rotation: 0 }]);
    setSelected(id);
    setDirty(true);
  }

  const rotate = () => {
    const p = pieces.find((q) => q.id === selected);
    if (p) update(p.id, { rotation: (((p.rotation + 90) % 360) as Rotation) });
  };
  const remove = () => {
    if (selected === undefined) return;
    setPieces((ps) => ps.filter((p) => p.id !== selected));
    setSelected(undefined);
    setDirty(true);
  };

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if ((e.target as HTMLElement).tagName === 'INPUT') return;
      if (e.key === 'r' || e.key === 'R') rotate();
      if (e.key === 'Delete' || e.key === 'Backspace') remove();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  });

  async function save() {
    if (!home) return;
    try {
      const r = await api.saveHome({ version: home.version, placements: pieces.map(({ item, x, y, rotation }) => ({ item, x, y, rotation })) });
      setHome({ ...home, version: r.version });
      setDirty(false);
      setMessage({ kind: 'ok', text: 'Saved.' });
    } catch (e) {
      setMessage({ kind: 'err', text: e instanceof ApiError ? e.message : 'Could not save.' });
    }
  }

  return (
    <div className="home">
      <div className="home-toolbar">
        <strong className="place">{ownerName}'s Cove Cottage</strong>
        <span className="spacer" />
        <button className="btn" onClick={() => (dirty && !window.confirm('Leave without saving?') ? undefined : onFarm())}>Farm →</button>
        <button className="btn ghost" onClick={onShop}>Buy furniture</button>
        <button className="btn ghost" onClick={rotate} disabled={selected === undefined}>Rotate (R)</button>
        <button className="btn ghost" onClick={remove} disabled={selected === undefined}>Put away (Del)</button>
        <button className="btn" onClick={() => void save()} disabled={!dirty}>{dirty ? 'Save layout' : 'Saved'}</button>
        <button className="btn ghost" onClick={() => (dirty && !window.confirm('Leave without saving?') ? undefined : onLeave())}>Back to town</button>
      </div>
      {message && <p className={message.kind === 'ok' ? 'note-ok center' : 'form-error center'} role="status">{message.text}</p>}
      <div className="home-body">
        <canvas
          ref={canvas}
          className="home-canvas"
          style={{ aspectRatio: `${HOME_ROOM.width} / ${HOME_ROOM.height}` }}
          onPointerDown={(e) => {
            const { x, y } = toRoom(e);
            const p = hit(x, y);
            setSelected(p?.id);
            if (p) {
              drag.current = { id: p.id, dx: x - p.x, dy: y - p.y };
              e.currentTarget.setPointerCapture(e.pointerId);
            }
          }}
          onPointerMove={(e) => {
            if (!drag.current) return;
            const { x, y } = toRoom(e);
            update(drag.current.id, { x: clamp(x - drag.current.dx, HOME_ROOM.width), y: clamp(y - drag.current.dy, HOME_ROOM.height) });
          }}
          onPointerUp={() => (drag.current = null)}
          aria-label="Your room. Drag furniture to move it."
          role="img"
        />
        <aside className="palette">
          <h3>Your furniture</h3>
          {home?.furniture.length === 0 && <p className="muted small-print">No furniture yet.</p>}
          {home?.furniture.map((f) => {
            const left = f.quantity - placedOf(f.key);
            return (
              <button key={f.key} className="palette-item" disabled={left <= 0} onClick={() => add(f)} title={`Place ${f.name}`}>
                <Thumb item={f} size={52} />
                <span>{f.name}</span>
                <span className="muted small-print">{left} left</span>
              </button>
            );
          })}
        </aside>
      </div>
    </div>
  );
}
